import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import ChatIcon from '../assets/li_chat.svg';
import MailIcon from '../assets/li_mail.svg';
import PhoneIcon from '../assets/li_phone.svg';
import { useMainContext } from '../context/MainContext.tsx';
import { useMergedConfig } from '../context/MergedConfigProvider.tsx';
import { useTranslations } from '../hooks/useTranslations.ts';
import { translationKeys } from '../translations/translationKeys.ts';
import { nonSpectreJourney } from '../utils/backgroundImage.ts';
import { resolveQueryRegion } from '../utils/resolveQueryRegion.ts';
import { useIsMobile } from '../utils/useIsMobile.ts';
import LinkButton from './LinkButton';

type ContactAction = {
  label: string;
  icon: string;
  alt: string;
  href?: string;
  onClick?: () => void;
};

const getPhoneContact = (journeyId?: string, region?: string | null): ContactAction => {
  const formattedRegion = region ? region.toLowerCase() : '';

  if (formattedRegion === 'ie' || formattedRegion === 'ireland') {
    return {
      label: '+353 (0)1 256 3000',
      icon: PhoneIcon,
      alt: 'Call support',
      href: 'tel:+353012563000',
    };
  }

  if (journeyId === 'spectre' || journeyId === 'spectreretail') {
    return {
      label: '+44 (0)203 003 8844',
      icon: PhoneIcon,
      alt: 'Call support',
      href: 'tel:+4402030038844',
    };
  }

  if (formattedRegion === 'de') {
    return {
      label: '+49 (0) 69 22 22 440 00',
      icon: PhoneIcon,
      alt: 'Call support',
      href: 'tel:+49069222244000',
    };
  }

  if (formattedRegion === 'at') {
    return {
      label: '+43 (0) 1 532 13 49 0',
      icon: PhoneIcon,
      alt: 'Call support',
      href: 'tel:+430153213490',
    };
  }

  if (formattedRegion === 'fr') {
    return {
      label: '+33 (0) 1 53 83 14 03',
      icon: PhoneIcon,
      alt: 'Call support',
      href: 'tel:+330153831403',
    };
  }

  if (formattedRegion === 'it') {
    return {
      label: '+39 020 3600 9604',
      icon: PhoneIcon,
      alt: 'Call support',
      href: 'tel:+3902036009604',
    };
  }

  if (formattedRegion === 'sg') {
    return {
      label: '+65 6559 6000',
      icon: PhoneIcon,
      alt: 'Call support',
      href: 'tel:+6565596000',
    };
  }

  return {
    label: '+44 (0) 20 7170 8209',
    icon: PhoneIcon,
    alt: 'Call support',
    href: 'tel:+442071708209',
  };
};

const getEmailContact = (region?: string | null): ContactAction => {
  const formattedRegion = region ? region.toLowerCase() : '';

  if (formattedRegion === 'ie' || formattedRegion === 'ireland') {
    return {
      label: 'info@cmcmarkets.ie',
      icon: MailIcon,
      alt: 'Email support',
      href: 'mailto:info@cmcmarkets.ie',
    };
  }

  if (formattedRegion === 'de') {
    return {
      label: 'kundenservice@cmcmarkets.de',
      icon: MailIcon,
      alt: 'Email support',
      href: 'mailto:kundenservice@cmcmarkets.de',
    };
  }

  if (formattedRegion === 'at') {
    return {
      label: 'kundenservice@cmcmarkets.at',
      icon: MailIcon,
      alt: 'Email support',
      href: 'mailto:kundenservice@cmcmarkets.at',
    };
  }

  if (formattedRegion === 'fr') {
    return {
      label: 'gestionclients@cmcmarkets.fr',
      icon: MailIcon,
      alt: 'Email support',
      href: 'mailto:gestionclients@cmcmarkets.fr',
    };
  }

  if (formattedRegion === 'it') {
    return {
      label: 'servizioclienti@cmcmarkets.it',
      icon: MailIcon,
      alt: 'Email support',
      href: 'mailto:servizioclienti@cmcmarkets.it',
    };
  }

  if (formattedRegion === 'sg') {
    return {
      label: 'support@cmcmarkets.com.sg',
      icon: MailIcon,
      alt: 'Email support',
      href: 'mailto:support@cmcmarkets.com.sg',
    };
  }

  return {
    label: 'clientmanagement@cmcmarkets.co.uk',
    icon: MailIcon,
    alt: 'Email support',
    href: 'mailto:clientmanagement@cmcmarkets.co.uk',
  };
};

interface ContactProps {
  isChatLoading: boolean;
  openLiveChatBtnOnClick: () => void;
}

export default function Contact({ isChatLoading, openLiveChatBtnOnClick }: ContactProps) {
  const { journeyId } = useMainContext();
  const { embedded, region: configRegion } = useMergedConfig();
  const isMobile = useIsMobile();
  const { translate } = useTranslations();

  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const urlRegion = resolveQueryRegion(query);
  const region = urlRegion || configRegion || undefined;

  const phoneContact = getPhoneContact(journeyId, region);
  const emailContact = getEmailContact(region);

  const items: ContactAction[] = [
    phoneContact,
    emailContact,
    ...(embedded || isMobile
      ? []
      : [
          {
            label: translate(translationKeys.START_A_CONVERSATION_NOW),
            icon: ChatIcon,
            alt: 'Open live chat',
            onClick: openLiveChatBtnOnClick,
          },
        ]),
  ];

  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-base font-normal leading-6 text-content-primary">
        {translate(translationKeys.CONTACT_WERE_HERE_FOR_YOU)}
      </h3>
      <div className="flex flex-col gap-4">
        {items.map(({ label, icon, alt, href, onClick }) => (
          <div key={label}>
            <LinkButton href={href} onClick={onClick} isLoading={!!onClick && isChatLoading}>
              <img
                src={icon}
                alt={alt}
                className="h-4 w-4"
                style={nonSpectreJourney(journeyId) ? { filter: 'brightness(0)' } : undefined}
              />
              <span>{label}</span>
            </LinkButton>
          </div>
        ))}
      </div>
    </section>
  );
}
